import { type Feature, type MultiPolygon, type Polygon, type Point as GeoJSONPoint } from 'geojson'
import {
  area,
  booleanIntersects,
  centerOfMass,
  featureCollection,
  intersect,
  polygon,
} from '@turf/turf'
import { type AreaData, closePolygon, isDefined, isEmpty, isNotEmpty, type LatLng, sqftToAcres } from '@terros/common'

type Point = [number, number]

export type AreaClipCandidate = {
  area: AreaData
  clippedCoords: LatLng[]
}

export function convertToPoint(latLng: LatLng): Point {
  return [latLng.longitude, latLng.latitude]
}

// Why: GMSPolygon (Google Maps iOS SDK) crashes in gmssdk::CoordsToPoints with
// a null path deref if a polygon ring has fewer than 3 vertices or any
// non-finite lat/lng. The Fabric mountChildComponentView path calls setMap:
// before the path is fully built, so the only safe interception is in JS —
// don't render <Polygon> when this returns false.
export function isRenderablePolygonRing(coords: LatLng[] | undefined): coords is LatLng[] {
  if (isEmpty(coords)) return false
  if (coords.length < 3) return false
  return coords.every((c) => Number.isFinite(c?.latitude) && Number.isFinite(c?.longitude))
}

export function pointToLatLng(point: GeoJSONPoint): LatLng {
  const [longitude, latitude] = point.coordinates
  return { latitude, longitude }
}

export function buildPointKey(point: GeoJSONPoint): string {
  const [longitude, latitude] = point.coordinates

  return `${latitude.toString()}|${longitude.toString()}`
}

type Coordinates = LatLng[]

export function convertPoints(coordinates: Coordinates): Point[] {
  return coordinates.map(convertToPoint)
}

export function convertToFeature(coordinates: Coordinates): Feature<Polygon> {
  const closedCoords = closePolygon(coordinates)
  return polygon([convertPoints(closedCoords)])
}

export function centerOfPolygon(coordinates: Coordinates): LatLng | undefined {
  try {
    const feature = convertToFeature(coordinates)
    const [x, y] = centerOfMass(feature).geometry.coordinates
    return { latitude: y, longitude: x }
  } catch (e) {
    console.error(e)
  }
}

const SQ_METERS_TO_SQ_FEET = 10.7639
const POLYGON_AREA_MATCH_THRESHOLD = 0.9999

export function calculatePolygonAcres(coordinates: Coordinates | undefined): number | undefined {
  if (isEmpty(coordinates) || coordinates.length < 3) return
  try {
    const feature = convertToFeature(coordinates)
    const sqMeters = area(feature)
    const sqFeet = sqMeters * SQ_METERS_TO_SQ_FEET
    return sqftToAcres(sqFeet)
  } catch (e) {
    console.error(e)
  }
}

export const areaToTurfPolygon = (area: Pick<AreaData, 'coordinates'>): Feature<Polygon> | undefined => {
  if (isEmpty(area.coordinates)) return
  const areaCoords = area.coordinates.map((coord) => [coord.longitude, coord.latitude])
  const firstCoord = areaCoords[0]
  const lastCoord = areaCoords[areaCoords.length - 1]
  if (firstCoord[0] !== lastCoord[0] || firstCoord[1] !== lastCoord[1]) {
    areaCoords.push(firstCoord)
  }
  return polygon([areaCoords])
}

export function isPolygonWithinBoundary(
  childCoords: LatLng[],
  boundaryAreas: Pick<AreaData, 'coordinates'>[]
): boolean {
  const validBoundaries = boundaryAreas
    .filter((a) => isNotEmpty(a.coordinates) && a.coordinates.length >= 3)
    .map(areaToTurfPolygon)
    .filter(isDefined)

  if (isEmpty(validBoundaries)) return true

  const child = convertToFeature(childCoords)
  const childArea = area(child)
  if (childArea <= 0) return false

  return validBoundaries.some((boundary) => {
    const clipped = intersect(featureCollection([child, boundary]))
    if (!clipped) return false
    return area(clipped) / childArea >= POLYGON_AREA_MATCH_THRESHOLD
  })
}

export function getOverlappingAreas(childCoords: LatLng[], siblingAreas: AreaData[]): AreaData[] {
  const child = convertToFeature(childCoords)
  return siblingAreas.filter((sibling) => {
    const siblingPoly = areaToTurfPolygon(sibling)
    if (!siblingPoly) return false
    return booleanIntersects(child, siblingPoly)
  })
}

export function getClipCandidates(childCoords: LatLng[], boundaryAreas: AreaData[]): AreaClipCandidate[] {
  const child = convertToFeature(childCoords)

  return boundaryAreas
    .flatMap((boundaryArea) => {
      const boundary = areaToTurfPolygon(boundaryArea)
      if (!boundary) return []

      const clipped = intersect(featureCollection([child, boundary]))
      const clippedCoords = toClipCoordinates(clipped)
      if (isEmpty(clippedCoords)) return []

      const overlapAcres = calculatePolygonAcres(clippedCoords)
      if (!isDefined(overlapAcres)) return []

      return [{ area: boundaryArea, clippedCoords, overlapAcres }]
    })
    .sort((a, b) => b.overlapAcres - a.overlapAcres)
}

function toClipCoordinates(clipped: Feature<Polygon | MultiPolygon> | null): LatLng[] | undefined {
  if (!clipped) return
  if (clipped.geometry.type === 'Polygon') return polygonRingToLatLngs(clipped.geometry.coordinates[0])

  const candidates = clipped.geometry.coordinates
    .map((polygonCoords) => {
      const outerRing = polygonCoords[0]
      const ringCoords = polygonRingToLatLngs(outerRing)
      if (isEmpty(ringCoords)) return

      const overlapAcres = calculatePolygonAcres(ringCoords)
      if (!isDefined(overlapAcres)) return

      return { ringCoords, overlapAcres }
    })
    .filter(isDefined)
    .sort((a, b) => b.overlapAcres - a.overlapAcres)

  return candidates.at(0)?.ringCoords
}

function polygonRingToLatLngs(points: number[][]): LatLng[] | undefined {
  if (isEmpty(points)) return

  const hasClosingPoint =
    points.length > 1 && points[0][0] === points[points.length - 1][0] && points[0][1] === points[points.length - 1][1]
  const openPoints = hasClosingPoint ? points.slice(0, -1) : points
  if (isEmpty(openPoints)) return

  return openPoints.map(([longitude, latitude]) => ({ latitude, longitude }))
}
